import { ColumnDef, Row } from "@tanstack/react-table";
import { Link } from "react-router-dom";

import { getDictionaryItemDescription } from "hooks/api/dictionary";

import { getTouchpointPaths } from "utilities/app-routes";
import { getDisplayTouchpointIcon, getDisplayTouchpointType, toSpacedTitleCase } from "utilities";
import { formatDateTime } from "utilities/date";

import {
  NamedDictionaryItem,
  TouchpointStatusDictionaryItem,
} from "hooks/api/dictionary/dictionary.types";

import { TouchpointType, TouchpointTypeLabel } from "types/touchpoint";

import { Chip } from "ui/chip";
import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";
import { TouchpointVersionHistoryDto } from "generated/schemas";
import { getTouchpointClassChipColor } from "utilities/touchpoint-class-chip";

interface TableActionsProps {
  audienceDictionaryItems: NamedDictionaryItem[];
  touchpointVersionStatuses: TouchpointStatusDictionaryItem[];
  isBatchManagementEnabled?: boolean;
  hasTemplates: boolean;
}

const headerStyles = "text-gray-600 font-bold leading-5 pl-4";

export const createReviewsTableColumns = ({
  audienceDictionaryItems,
  isBatchManagementEnabled,
  hasTemplates,
}: TableActionsProps): ColumnDef<TouchpointVersionHistoryDto>[] =>
  [
    isBatchManagementEnabled && {
      id: "touchpointBatchId",
      accessorKey: "touchpointBatchId",
      enableSorting: false,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "touchpointType",
      header: "Type",
      accessorKey: "touchpointType",
      enableSorting: true,
      cell: ({ row }: { row: Row<TouchpointVersionHistoryDto> }) => {
        const touchpointType = row.original.touchpointType as TouchpointType;
        const isTemplate = row.original.campaignName === PROGRAM_TEMPLATES_CAMPAIGN_NAME;
        const icon = getDisplayTouchpointIcon(touchpointType);
        const title =
          touchpointType === TouchpointType.OTHER
            ? TouchpointTypeLabel.OTHER
            : getDisplayTouchpointType(touchpointType);

        return (
          <div className="flex items-center gap-2 whitespace-nowrap">
            <div className="size-4 mt-0.5">{icon}</div>
            <span className="text-gray-800 font-medium">{title}</span>
            {isTemplate && <Chip variant="filled" size="small" label="Template" />}
          </div>
        );
      },
      size: hasTemplates ? 160 : 120,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "touchpointName",
      header: "Creative version",
      accessorKey: "touchpointName",
      enableSorting: true,
      cell: ({ row }: { row: Row<TouchpointVersionHistoryDto> }) => {
        const { touchpointPreviewPath } = getTouchpointPaths({
          clientId: row.original.clientId ?? "",
          campaignId: row.original.campaignId ?? "",
          touchpointId: row.original.touchpointId ?? "",
        });

        return (
          <div className="flex flex-col pr-3">
            <Link
              to={touchpointPreviewPath}
              className="text-aqua-600 hover:text-aqua-700 font-semibold line-clamp-1"
              aria-disabled={false}
              tabIndex={undefined}>
              {row.original.touchpointName}
            </Link>
            <div>
              <span>v {row.original.touchpointVersionVersion}</span>
            </div>
          </div>
        );
      },
      size: 220,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "campaignName",
      header: "Campaign name",
      accessorKey: "campaignName",
      enableSorting: true,
      cell: ({ row }: { row: Row<TouchpointVersionHistoryDto> }) => (
        <div>
          <span className="line-clamp-1">{row.original.campaignName}</span>
        </div>
      ),
      size: 220,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "campaignAudiences",
      header: "Audience",
      accessorKey: "campaignAudiences",
      enableSorting: false,
      cell: ({ row }: { row: Row<TouchpointVersionHistoryDto> }) => {
        const audienceDescriptions = row.original.campaignAudiences?.map((audienceName) => ({
          name: audienceName,
          description: getDictionaryItemDescription(audienceDictionaryItems, audienceName),
        }));

        return (
          <div className="flex flex-wrap">
            {audienceDescriptions?.map(({ name, description }, index) => (
              <span key={name} className="whitespace-nowrap">
                {description}
                {index < audienceDescriptions.length - 1 && ",\u00A0"}
              </span>
            ))}
          </div>
        );
      },
      size: 220,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "populationType",
      header: "Population type",
      accessorKey: "populationType",
      enableSorting: true,
      cell: ({ row }: { row: Row<TouchpointVersionHistoryDto> }) =>
        row.original.populationType ? (
          <Chip
            size="small"
            variant="outlined"
            label={toSpacedTitleCase(row.original.populationType)}
          />
        ) : null,
      size: 150,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "classes",
      header: "Class",
      accessorKey: "classes",
      enableSorting: false,
      cell: ({ row }: { row: Row<TouchpointVersionHistoryDto> }) => {
        if (!row.original.classes?.length) {
          return null;
        }

        return (
          <div className="flex flex-wrap gap-1">
            {row.original.classes.map((classType) => (
              <Chip
                key={classType}
                size="small"
                variant="outlined"
                color={getTouchpointClassChipColor(classType)}
                label={toSpacedTitleCase(classType)}
              />
            ))}
          </div>
        );
      },
      size: 120,
      meta: {
        headerClassName: headerStyles,
      },
    },
    {
      id: "lastModifiedDate",
      header: "Last updated",
      accessorKey: "lastModifiedDate",
      enableSorting: true,
      cell: ({ row }: { row: Row<TouchpointVersionHistoryDto> }) => (
        <div className="text-gray-600">{formatDateTime(row.original.lastModifiedDate)}</div>
      ),
      size: 200,
      meta: {
        headerClassName: headerStyles,
      },
    },
  ].filter(Boolean) as ColumnDef<TouchpointVersionHistoryDto>[];
